import type { ReactElement } from 'react';
import { useCallback, useState } from 'react';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { Button, Modal } from '@hotjar/hotsauce';
import {
  EmailVerificationComponent,
  EmailVerificationResult,
} from '@hotjar/webapp/features/EmailVerification/components';
import { Trans } from 'react-i18next';
import styled from '@emotion/styled';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';

const ChangeEmailLink = styled.button`
  display: inline;
  text-decoration: underline;
  background: none;
  border: none;
  padding: 0;
`;

export interface VerifyEmailModalContentsProps {
  userEmail: string;
  onClickChangeEmail: () => void;
  onEmailVerified: () => void;
  onClose: () => void;
  onNext: () => void;
}

export const VerifyEmailModalContents = ({
  userEmail,
  onClickChangeEmail,
  onEmailVerified,
  onClose,
  onNext,
}: VerifyEmailModalContentsProps): ReactElement => {
  const { t } = useTranslationWithPrefix('mfa:enable-modal.verify-email');
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const { click } = useTrack();

  const onEmailLinkClick = (provider: string): void => {
    click(provider, 'Change Email Form', {});
  };

  const handleEmailVerification = useCallback(
    (result: EmailVerificationResult) => {
      onEmailVerified();
      if (result === EmailVerificationResult.AlreadyVerified) {
        onNext();
      }
      setIsSuccess(true);
    },
    [onEmailVerified, onNext],
  );

  return (
    <>
      <Modal.Body>
        <EmailVerificationComponent
          userEmail={userEmail}
          onEmailVerified={handleEmailVerification}
          onLoadingChange={setLoading}
          onEmailLinkClick={onEmailLinkClick}
          footer={() => (
            <Trans i18nKey="mfa:enable-modal:verify-email.cannot-find-email-message">
              <ChangeEmailLink data-testid="change-email-link" onClick={onClickChangeEmail}>
                change your email address
              </ChangeEmailLink>
            </Trans>
          )}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button kind="ghost" onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button
          kind="primary"
          isLoading={isLoading}
          isDisabled={!isSuccess}
          onClick={onNext}
          type="submit"
        >
          {t('action')}
        </Button>
      </Modal.Footer>
    </>
  );
};
